<script lang="ts" setup>
import {
  Smile,
  AtSign,
  Heading1,
  Heading2,
  Heading3,
  Bold,
  Link,
  Italic,
  Underline,
  Strikethrough
} from 'lucide-vue-next'
import type { Emitter } from 'mitt'

const props = defineProps<{
  emitter: Emitter<RichTextEvent>
  disabled?: boolean
  sticker?: boolean
  mention?: boolean
  image?: boolean
  customImage?: boolean
  heading?: boolean
  bold?: boolean
  italic?: boolean
  underline?: boolean
  strike?: boolean
  link?: boolean
}>()

const emit = defineEmits(['onCustomImageUpload'])

const iconColor = computed(() =>
  props.disabled ? 'var(--color-gray-200)' : 'var(--color-gray-400)'
)

function insertMention() {
  props.emitter.emit('insert:text', '@')
}

function onInsertImg(data?: { url?: string; file?: File }) {
  props.emitter.emit('insertImg:submit', data)
}

function onInsertImgCustom(data?: { url?: string; file?: File }) {
  emit('onCustomImageUpload', data)
}

function insertSticker(name: string) {
  props.emitter.emit('sticker:insert', name)
}

function insertLink(link: { text: string; href: string }) {
  props.emitter.emit('insert:link', link)
}
</script>

<template>
  <div class="rich-text-toolbar" :class="{ disabled }">
    <MSpace align="center" class="rich-text-toolbar__actions" gap="4px">
      <rich-text-insert-sticker v-if="sticker" @submit="insertSticker">
        <m-icon>
          <Smile :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </rich-text-insert-sticker>
      <rich-text-insert-mention v-if="mention" @submit="insertMention">
        <m-icon>
          <AtSign :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </rich-text-insert-mention>
      <rich-text-insert-img
        v-if="image"
        :disabled="disabled"
        @on-success="onInsertImg"
      />
      <rich-text-insert-img
        v-if="customImage"
        :disabled="disabled"
        @on-success="onInsertImgCustom"
      />
      <button v-if="heading" type="button" class="ql-header" value="1">
        <m-icon>
          <Heading1
            :color="iconColor"
            :size="32"
            absolute-stroke-width
            :stroke-width="1.1"
          />
        </m-icon>
      </button>
      <button v-if="heading" type="button" class="ql-header" value="2">
        <m-icon>
          <Heading2
            :color="iconColor"
            :size="32"
            absolute-stroke-width
            :stroke-width="1.1"
          />
        </m-icon>
      </button>
      <button v-if="heading" type="button" class="ql-header" value="3">
        <m-icon>
          <Heading3
            :color="iconColor"
            :size="32"
            absolute-stroke-width
            :stroke-width="1.1"
          />
        </m-icon>
      </button>
      <button v-if="bold" type="button" class="ql-bold">
        <m-icon>
          <Bold :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </button>
      <button v-if="italic" type="button" class="ql-italic">
        <m-icon>
          <Italic :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </button>
      <button v-if="underline" type="button" class="ql-underline">
        <m-icon>
          <Underline :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </button>
      <button v-if="strike" type="button" class="ql-strike">
        <m-icon>
          <Strikethrough :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </button>
      <rich-text-insert-link v-if="link" @submit="insertLink">
        <m-icon>
          <Link :color="iconColor" :stroke-width="1.5" />
        </m-icon>
      </rich-text-insert-link>
      <div class="custom-button">
        <slot name="custom" />
      </div>
      <div class="slot">
        <slot />
      </div>
    </MSpace>
  </div>
</template>

<style lang="scss" scoped>
.rich-text-toolbar {
  background: #fff;
  padding: 8px 16px;
  position: sticky;
  top: 72px;
  z-index: 500;
  border: 1px 1px 0 1px solid var(--color-gray-100);
  button {
    border: none;
    background: transparent;
    padding: 0;
  }
  .m-icon {
    cursor: pointer;
    font-size: 24px;
    color: var(--color-gray-400);
  }
  :deep(.ql-active) {
    svg {
      stroke: var(--color-sky-500);
      color: var(--color-sky-500);
      stroke-width: 2;
    }
  }
  &__actions {
    display: flex;
    button {
      display: flex;
      align-items: center;
    }
  }
  .slot {
    margin-left: auto;
  }
}
.disabled {
  pointer-events: none;
}
</style>
